'use client';
import React, {
  createContext,
  useContext,
  useOptimistic,
  useMemo,
  useState,
  useCallback
} from 'react';
import { SavedList, SavedListsResponse } from 'services/savedLists/types';
import { SAVED_LIST_ACTIONS } from './_constants/common';
import { UserCustomerTypes } from 'services/user/types';
import { getSavedLists as getSavedListsAction } from 'services/savedLists/actions';
import { parse } from 'query-string';
import { SavedListsContextProps } from './types';
import { transformQueryParams } from './utils';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import {
  gaSearchValidationError,
  getSearchResultsFormEvent
} from './analytics-utils';
import { useManageUserAttributes } from 'hooks/use-manage-user-attributes';

const SavedListsContext = createContext<SavedListsContextProps>(null);
export const useSavedList = () => useContext(SavedListsContext);

export const SavedListsProvider = ({
  children,
  shoppingLists,
  error,
  customerType,
  locationId = '',
  orderData = {},
  minimizeSpecialCharactersFeatureFlag = false
}) => {
  const { DELETE, ADD, ADD_AS_FAVORITE, REMOVE_AS_FAVORITE } =
    SAVED_LIST_ACTIONS;
  const [savedList, setSavedList] = useState<SavedList[]>(shoppingLists);
  const [savedListsError, setSavedListsError] = useState<boolean>(error);
  const [showSkeletonLoader, setShowSkeletonLoader] = useState<boolean>(false);
  const [optimisticSavedList, setOptimisticSavedList] = useOptimistic(
    savedList,
    (
      state,
      { action, listItem }: { action: string; listItem: SavedList }
    ): SavedList[] => {
      switch (action) {
        case DELETE:
          return state.filter(
            (item) => item.shoppingListId !== listItem.shoppingListId
          );
        case ADD_AS_FAVORITE:
          return [
            ...state.map((item) => {
              if (item.shoppingListId === listItem.shoppingListId) {
                return { ...item, isFavorite: true };
              }
              return item;
            })
          ];
        case REMOVE_AS_FAVORITE:
          return [
            ...state.map((item) => {
              if (item.shoppingListId === listItem.shoppingListId) {
                return { ...item, isFavorite: false };
              }
              return item;
            })
          ];
        case ADD:
        default:
          return [...state, listItem];
      }
    }
  );

  const [isPending, setIsPending] = useState(false);
  const [isManageViewPreferencesToggled, setIsManageViewPreferencesToggled] =
    useState(false);
  const isDealerUser = customerType === UserCustomerTypes.Dealer;

  const { getManagedUserAttributes } = useManageUserAttributes();

  const { fireEvent } = useFormSubmitEvent();

  const getSavedLists = useCallback(
    async (onSubmitFireEvent = false) => {
      const queryParameters = transformQueryParams(
        typeof window !== 'undefined'
          ? parse(window.location.search, { parseBooleans: true })
          : {}
      );
      const {
        savedList_showOnlyMyFavorites: isFavorite,
        savedList_showOnlyMyList: isSelfShoppingList
      } = await getManagedUserAttributes();
      try {
        const queryParams = {
          ...queryParameters,
          createdBy: queryParameters.createdBy
            ? atob(queryParameters.createdBy)
            : '',
          isFavorite,
          isSelfShoppingList,
          ...(isDealerUser && { isSelfShoppingList: false })
        };
        const { shoppingLists = [] }: SavedListsResponse =
          await getSavedListsAction(queryParams);
        setSavedList(shoppingLists);
        setShowSkeletonLoader(false);
        setSavedListsError(false);
        if (onSubmitFireEvent) {
          fireEvent(
            getSearchResultsFormEvent({
              savedListsLength: shoppingLists?.length,
              itemPartNumber: queryParameters.partNumber,
              startDate: queryParameters.createdOrUpdatedStartDate,
              endDate: queryParameters.createdOrUpdatedEndDate
            })
          );
        }
      } catch (error) {
        console.log(error);
        setSavedList([]);
        setShowSkeletonLoader(false);
        setSavedListsError(true);
        if (onSubmitFireEvent) {
          fireEvent(
            gaSearchValidationError({
              itemPartNumber: queryParameters.partNumber,
              startDate: queryParameters.createdOrUpdatedStartDate,
              endDate: queryParameters.createdOrUpdatedEndDate
            })
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDealerUser]
  );

  const propsValues = useMemo(
    () => ({
      optimisticSavedList,
      setOptimisticSavedList,
      setSavedList,
      savedListsError,
      setSavedListsError,
      isPending,
      setIsPending,
      showSkeletonLoader,
      setShowSkeletonLoader,
      isDealerUser,
      getSavedLists,
      isManageViewPreferencesToggled,
      setIsManageViewPreferencesToggled,
      locationId,
      orderData,
      minimizeSpecialCharactersFeatureFlag
    }),
    [
      optimisticSavedList,
      savedListsError,
      showSkeletonLoader,
      setShowSkeletonLoader,
      setOptimisticSavedList,
      setSavedList,
      setSavedListsError,
      isPending,
      setIsPending,
      isDealerUser,
      getSavedLists,
      isManageViewPreferencesToggled,
      setIsManageViewPreferencesToggled,
      locationId,
      orderData,
      minimizeSpecialCharactersFeatureFlag
    ]
  );

  return (
    <SavedListsContext.Provider value={propsValues}>
      {children}
    </SavedListsContext.Provider>
  );
};
